import { memo } from 'react';

import { type Board } from '@air/api/types';
import { Button } from '@air/primitive-button';

import { BoardSelectBoardListItem } from '~/components/BoardSelect/BoardSelectBoardListItem';
import { BoardSelectListItem } from '~/components/BoardSelect/components/BoardSelectListItem';
import { useLibraryRootBoards } from '~/components/LibraryBeta/hooks/queries/useLibraryRootBoards';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

type Library = Required<Board>['library'];

export type BoardSelectLibraryListItemProps = {
  canSelect?: boolean;
  getBoardDisabledMessage?: (board: Board) => string | undefined;
  getLibraryDisabledMessage?: (board: Library) => string | undefined;
  initialBoardId: Board['id'] | undefined;
  isBoardDisabled?: (board: Board) => boolean;
  isBoardExpanded?: (id: Board['id']) => boolean;
  isBoardSelected?: (board: Board) => boolean;
  isLibraryDisabled?: (library: Library) => boolean;
  isLibraryExpanded?: (library: Library) => boolean;
  isLibrarySelected?: (library: Library) => boolean;
  library: Library;
  onBoardOpenChange: (params: { id: Board['id']; isOpen?: boolean }) => void;
  onInitialBoardRow: (row: HTMLDivElement | null) => void;
  onLibraryOpenChange: (params: { id: Library['id']; isOpen?: boolean }) => void;
  onSelectBoard?: (board: Board | null) => void;
  onSelectLibrary?: (library: Library | undefined) => void;
  search?: string;
  allowCreateSubBoard?: boolean;
  shouldShowSubBoards?: (id: Board['id']) => boolean;
};

export const BoardSelectLibraryListItem = memo(
  ({
    canSelect,
    isBoardDisabled,
    isBoardExpanded,
    isBoardSelected,
    getBoardDisabledMessage,
    getLibraryDisabledMessage,
    initialBoardId,
    isLibraryDisabled,
    isLibraryExpanded,
    isLibrarySelected,
    library,
    onBoardOpenChange,
    onInitialBoardRow,
    onLibraryOpenChange,
    onSelectBoard,
    onSelectLibrary,
    allowCreateSubBoard,
    search,
    shouldShowSubBoards,
  }: BoardSelectLibraryListItemProps) => {
    const isExpanded = isLibraryExpanded?.(library);
    const isDisabled = isLibraryDisabled?.(library);
    const isSelected = isLibrarySelected?.(library);

    const { currentWorkspace } = useCurrentWorkspace();
    const {
      data: boards = [],
      isLoadingMore,
      hasMore,
      loadNextPage,
    } = useLibraryRootBoards({
      workspaceId: currentWorkspace?.id,
      libraryId: library.id,
      params: { search },
      enabled: isExpanded,
    });

    useFetchObjectsPermissions({
      objects: {
        boardIds: boards.map((board) => board.id),
      },
    });

    return (
      <BoardSelectListItem
        disabled={isDisabled}
        disabledMessage={getLibraryDisabledMessage?.(library)}
        id={library.id}
        isExpanded={isExpanded}
        isSelected={canSelect ? isSelected : false}
        // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing inline handler, flagged by changed-file lint
        onOpenChange={(isOpen) => onLibraryOpenChange({ id: library.id, isOpen })}
        // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing inline handler, flagged by changed-file lint
        onSelect={() =>
          canSelect
            ? onSelectLibrary?.(isSelected ? undefined : library)
            : onLibraryOpenChange({ id: library.id, isOpen: !isExpanded })
        }
        prefix={
          <div
            className="flex size-6 items-center justify-center rounded-4 ads:bg-subtle bg-pigeon-50"
            style={{ backgroundColor: library.color?.backgroundHex }}
          >
            {library.icon ?? '📚'}
          </div>
        }
        title={library.title}
      >
        {boards.map((board) => {
          return (
            <BoardSelectBoardListItem
              allowCreateSubBoard={allowCreateSubBoard}
              board={board}
              getBoardDisabledMessage={getBoardDisabledMessage}
              initialBoardId={initialBoardId}
              isBoardDisabled={isBoardDisabled}
              isBoardExpanded={isBoardExpanded}
              isBoardSelected={isBoardSelected}
              key={board.id}
              onBoardOpenChange={onBoardOpenChange}
              onInitialBoardRow={onInitialBoardRow}
              onSelectBoard={onSelectBoard}
              shouldShowSubBoards={shouldShowSubBoards}
            />
          );
        })}

        {!isLoadingMore && hasMore && (
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing inline handler, flagged by changed-file lint
          <Button onClick={() => loadNextPage()} size="small" appearance="ghost" color="gray">
            Show more
          </Button>
        )}
      </BoardSelectListItem>
    );
  },
);

BoardSelectLibraryListItem.displayName = 'BoardSelectLibraryListItem';
